import { createElement } from 'react';
import { KText } from '@/ui-library/components/text';
import {
  Box,
  Button,
  Icon,
  Tooltip,
  Flex,
  Menu,
  MenuButton as ChakraMenuButton,
  MenuList,
  MenuItem,
  Portal,
} from '@chakra-ui/react';

const MenuButton = ChakraMenuButton as any;
import { MdOutlineMoreVert } from 'react-icons/md';
import { ChevronDown, ChevronRight } from '@kula-ai/icons-core';

const menuRowHover = {
  backgroundColor: 'rgb(246, 247, 247)',
  '.more-menu-item': { fontWeight: '650' },
};

export const MoreMenu = ({
  menuItems = [],
  handleActionClick = () => {},
  enableTrippleDot = false,
  isOpen,
  onOpen,
  onClose,
}: any) => {
  return (
    <Menu
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="bottom-end"
      closeOnSelect={false}
      isLazy
    >
      <MenuButton
        as={enableTrippleDot ? Box : Button}
        cursor="pointer"
        width={enableTrippleDot ? '18px' : undefined}
        height={enableTrippleDot ? '18px' : undefined}
        display="inline-block"
        data-test-id="candidate-detail-more-button"
        variant={enableTrippleDot ? undefined : 'tertiary'}
        maxH={enableTrippleDot ? undefined : '34px'}
        px={enableTrippleDot ? undefined : '12px'}
        fontSize={enableTrippleDot ? undefined : 'sm'}
        rightIcon={!enableTrippleDot ? <Icon as={ChevronDown} /> : undefined}
      >
        {enableTrippleDot ? (
          <Icon
            as={MdOutlineMoreVert as any}
            width="18px"
            height="18px"
            color="secondary-700"
            verticalAlign="top"
          />
        ) : (
          'More'
        )}
      </MenuButton>
      <MenuList zIndex="99" minW="300px" py="5px" mr="-20px" mt="10px">
        {menuItems?.map((item) =>
          item?.type === 'custom' ? (
            <Box key={item.id}>{createElement(item.component, item.props)}</Box>
          ) : item?.type === 'submenu' && item?.children?.length > 0 ? (
            <Menu key={item.id} placement="right-start" isLazy gutter={14}>
              <Tooltip
                label={item?.tooltip}
                hasArrow
                closeDelay={
                  !item?.disabled && item?.closeDelay ? 3000 : undefined
                }
              >
                <MenuButton
                  as={MenuItem}
                  icon={
                    item?.icon ? (
                      <Icon as={item.icon} w="16px" h="16px" />
                    ) : undefined
                  }
                  closeOnSelect={false}
                  isDisabled={item?.disabled}
                  opacity={item?.disabled ? 0.5 : 1}
                  _hover={item?.disabled ? {} : menuRowHover}
                  _disabled={{ pointerEvents: 'auto', cursor: 'not-allowed' }}
                  data-test-id={`candidate-detail-more-menu-${item?.id}-option`}
                >
                  <Flex justify="space-between" align="center" w="100%">
                    <KText
                      className="more-menu-item"
                      size="sm-regular"
                      color="secondary-900"
                    >
                      {item?.label}
                    </KText>
                    <Icon
                      as={ChevronRight}
                      w="16px"
                      h="16px"
                      color="secondary-700"
                      flexShrink={0}
                      ml="8px"
                    />
                  </Flex>
                </MenuButton>
              </Tooltip>
              <Portal>
                <MenuList zIndex="9999" minW="300px" py="5px">
                  {item.children.map((child) => {
                    const isAiAdhocSchedule = child.id === 'aiAdhoc';
                    const showAiGradient =
                      isAiAdhocSchedule && !child?.disabled;
                    const menuItem = (
                      <MenuItem
                        key={child.id}
                        icon={
                          child?.icon ? (
                            <Icon
                              as={child.icon}
                              w="16px"
                              h="16px"
                              color={
                                showAiGradient ? 'secondary-700' : undefined
                              }
                            />
                          ) : undefined
                        }
                        isDisabled={child?.disabled}
                        opacity={child?.disabled ? 0.5 : 1}
                        _hover={child?.disabled ? {} : menuRowHover}
                        onClick={() => {
                          if (child?.disabled) return;
                          onClose?.();
                          handleActionClick(child.id);
                        }}
                        data-test-id={`candidate-detail-more-menu-${child?.id}-option`}
                      >
                        <KText
                          className="more-menu-item"
                          size="sm-regular"
                          fontWeight={showAiGradient ? '650' : undefined}
                          {...(showAiGradient
                            ? {
                                bgGradient: 'linear(to-b, #8A90FF, #B15FDA)',
                                bgClip: 'text',
                              }
                            : { color: 'secondary-900' })}
                        >
                          {child?.label}
                        </KText>
                      </MenuItem>
                    );
                    return child?.tooltip ? (
                      <Tooltip
                        key={child.id}
                        label={child.tooltip}
                        hasArrow
                        placement="left"
                      >
                        <Box as="span" display="block">
                          {menuItem}
                        </Box>
                      </Tooltip>
                    ) : (
                      menuItem
                    );
                  })}
                </MenuList>
              </Portal>
            </Menu>
          ) : (
            <Tooltip
              key={item.id}
              label={item?.tooltip}
              hasArrow
              // The long close-delay is for an item's own read-me tooltip (e.g. survey); a
              // consent-blocked row must not keep its tooltip lingering as you move away.
              closeDelay={
                !item?.disabled && item?.closeDelay ? 3000 : undefined
              }
            >
              <MenuItem
                icon={
                  item?.icon ? (
                    <Icon
                      as={item.icon}
                      w="16px"
                      h="16px"
                      {...item?.iconProps}
                    />
                  ) : undefined
                }
                isDisabled={item?.disabled}
                opacity={item?.disabled ? 0.5 : 1}
                _hover={item?.disabled ? {} : menuRowHover}
                // Keep pointer events on a disabled row so its tooltip's mouseleave still fires —
                // otherwise the previous row's tooltip stays stuck. The onClick guard below keeps
                // it non-actionable.
                _disabled={{ pointerEvents: 'auto', cursor: 'not-allowed' }}
                onClick={() => {
                  if (item?.disabled) return;
                  onClose?.();
                  handleActionClick(item?.id);
                }}
                data-test-id={`candidate-detail-more-menu-${item?.id}-option`}
              >
                <KText
                  className="more-menu-item"
                  size="sm-regular"
                  color="secondary-900"
                >
                  {item?.label}
                </KText>
              </MenuItem>
            </Tooltip>
          )
        )}
      </MenuList>
    </Menu>
  );
};
